import { singleton, injectAll } from 'tsyringe';
import { StopWatch } from '../../../odk-ts';
import { DelegateProvider } from '../../../shared/interfaces/delegate-provider.interface';
import { LoggerService } from '../../../shared/services/logger.service';
import { Game } from '../../models/game.model';
import { RecommendationDatastoreService } from '../datastore-service/recommendation-datastore.service';
import { ConditionContext } from './conditions/interfaces/condition-context.interface';
import {
  kNameOfCondition,
  ICondition,
  MatchResult,
} from './conditions/interfaces/condition.interface';
import { WeightedRandomAppSelector } from './weighted-random-app-selector';
import { NoRecommendationReasons } from './no-recommendations-reasons.enums';
import {
  ConditionType,
  getConditionType,
} from './conditions/decrators/condition-type.decorator';
import { Promotion } from '../../models/promotion.model';
import { PromotionAnalyticsService } from '../promotion-analytics/promotion-analytics.service';
import { PromotionType } from '../datastore-service/models/raw-promotion.model';

// -----------------------------------------------------------------------------
export interface IContentRecommendationServiceDelegate {
  onNoRecommendations?(reason: NoRecommendationReasons): void;
  onShowPromotion?(game: Game, promotion: any): void;
}

// -----------------------------------------------------------------------------
@singleton()
export class ContentRecommendationService
  implements DelegateProvider<IContentRecommendationServiceDelegate>
{
  private readonly logger = LoggerService.getCategory(
    'ContentRecommendationService',
  );

  private readonly listeners: Set<IContentRecommendationServiceDelegate>;

  private skipShowingPromotion: boolean = false;
  private skipShowingPromotionReason: NoRecommendationReasons = null;

  // ---------------------------------------------------------------------------
  public constructor(
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly weightedRandomAppSelector: WeightedRandomAppSelector,
    @injectAll(kNameOfCondition)
    private readonly conditions: ICondition[],
    private readonly promotionAnalyticsService: PromotionAnalyticsService,
    private readonly stopWatch: StopWatch,
  ) {
    this.listeners = new Set<IContentRecommendationServiceDelegate>();
  }

  // ---------------------------------------------------------------------------
  public async gameStarted(gameId: number) {
    this.stopWatch.start();
    this.logger.info(`Game Id ${gameId} started`);

    // this.promotionAnalyticsService.reportAppLaunched(gameId);

    const game = await this.getAndHandleGameConfiguration(gameId);
    if (!game) {
      this.triggerNoRecommendation(NoRecommendationReasons.ConfiguredForEnd);
      return;
    }

    if (!game.shouldShowOnStart) {
      this.logger.info('Game configured to wait for game to end ...');
      this.triggerNoRecommendation(NoRecommendationReasons.ConfiguredForEnd);
      return;
    }

    return this.runConditions(game);
  }

  // ---------------------------------------------------------------------------
  // TODO(advoyris): Need to rethink the edge-case where shouldShowOnStart is
  // false but we have a |game.targetGameId|
  public async gameEnded(gameId: number) {
    const game = await this.getAndHandleGameConfiguration(gameId);

    if (!game) {
      this.triggerNoRecommendation(NoRecommendationReasons.NoGameConfig);
      return;
    }

    if (game.shouldShowOnStart) {
      this.triggerNoRecommendation(NoRecommendationReasons.ConfiguredForStart);
      return;
    }

    if (!game.shouldShowOnEnd) {
      return;
    }

    const configs = await this.datastoreService.getGlobalConfigurations();

    const timeSinceGameStartedInMin = this.stopWatch.getTime() / 60 / 1000;
    this.stopWatch.stop();

    this.logger.info(
      `Game Id ${gameId} ended, after running for ` +
        `${timeSinceGameStartedInMin} minutes out of the configured ` +
        `${configs.minimumGameSessionTimeToDisplayPromotionMins} minutes `,
    );

    if (
      timeSinceGameStartedInMin <
      configs.minimumGameSessionTimeToDisplayPromotionMins
    ) {
      this.logger.info(
        'Skipping recommendation display because game session was to short',
      );

      this.triggerNoRecommendation(
        NoRecommendationReasons.MinimumGameSessionLengthNotReached,
      );

      return;
    }

    this.runConditions(game);
  }

  // ---------------------------------------------------------------------------
  private async getAndHandleGameConfiguration(gameId: number): Promise<Game> {
    try {
      const game = await this.datastoreService.getGame(gameId);

      if (!game) {
        this.logger.info('No game configuration found');
        this.triggerNoRecommendation(NoRecommendationReasons.NoGameConfig);
        return null;
      }

      return game;
    } catch (ex) {
      this.logger.error('', ex);
      this.triggerNoRecommendation(NoRecommendationReasons.FailedToQueryServer);
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // TODO(twolf): Document
  private async runConditions(game: Game): Promise<void> {
    this.logger.info(
      '*** Starting promotion display conditions evaluation process ...',
    );

    const [passed, reason] = await this.getGlobalConditionsResult(game);
    if (!passed) {
      // this.promotionAnalyticsService.reportGlobalConditionResult({
      //   gameId: game.id,
      //   reason,
      // });
      return this.triggerNoRecommendation(reason);
    }

    await this.tryRecommendingPromotion(game);
  }

  // ---------------------------------------------------------------------------
  private async getGlobalConditionsResult(game: Game): Promise<MatchResult> {
    try {
      const context: ConditionContext = { game };

      const globalConditions = this.conditions.filter(
        (condition) => getConditionType(condition) === ConditionType.Global,
      );

      return await this.passesConditions(context, globalConditions);
    } catch (err) {
      this.logger.error('Failed checking global conditions: ', err);
    }

    return [false, NoRecommendationReasons.CooldownCrashed];
  }

  // ---------------------------------------------------------------------------
  // We try recommending first from offers, if the offer was already approved
  // by the user, we then try recommending from promotions.
  // If the offer was not approved, we skip recommending any regular promotions.
  private async tryRecommendingPromotion(game: Game) {
    // Try offers
    const showedFromOffers = await this.evaluateAndShowFirstPassing(
      game,
      game.offers,
    );
    if (showedFromOffers) {
      return;
    }

    if (this.skipShowingPromotion) {
      this.triggerNoRecommendation(this.skipShowingPromotionReason);
      return;
    }

    // Try promotions
    const showedFromPromotions = await this.evaluateAndShowFirstPassing(
      game,
      game.promotions,
    );
    if (showedFromPromotions) {
      return;
    }

    this.triggerNoRecommendation(NoRecommendationReasons.PortfolioExhausted);
  }

  // ---------------------------------------------------------------------------
  private async evaluateAndShowFirstPassing(
    game: Game,
    promotions: Promotion[],
  ): Promise<boolean> {
    if (!promotions || promotions.length === 0) {
      return false;
    }

    const nonGlobalConditions = this.conditions.filter(
      (cond) => getConditionType(cond) === ConditionType.Promotion,
    );

    this.weightedRandomAppSelector.initialize(promotions);

    let selectedPromotion = this.weightedRandomAppSelector.next();

    // eslint-disable-next-line no-unmodified-loop-condition
    while (selectedPromotion) {
      const context: ConditionContext = { game, promotion: selectedPromotion };

      this.logger.info(
        `**** Running conditions on: ${selectedPromotion.name} ...`,
      );

      const [passed, reason] = await this.passesConditions(
        context,
        nonGlobalConditions,
      );

      if (passed) {
        this.triggerShowPromotion(game, selectedPromotion);
        return true;
      }

      this.processPromotionReason(selectedPromotion, reason);

      selectedPromotion = this.weightedRandomAppSelector.next();
    }

    return false;
  }

  // ---------------------------------------------------------------------------
  private async passesConditions(
    context: ConditionContext,
    conditions: ICondition[],
  ): Promise<MatchResult> {
    for (const condition of conditions) {
      const shouldSkip = await this.shouldSkipCondition(condition, context);
      if (shouldSkip) {
        this.logger.info(`Skipping condition ${condition.id}`);
        continue;
      }

      const [passed, reason] = await condition.match(context);

      if (!passed) {
        this.logger.info(`${condition.id}: [Failed] (${reason})`);
        return [passed, reason];
      }

      this.logger.info(`${condition.id}: [Passed]`);
    }

    return [true];
  }

  // ---------------------------------------------------------------------------
  private processPromotionReason(
    promotion: Promotion,
    reason: NoRecommendationReasons,
  ) {
    if (!reason) {
      return;
    }

    if (promotion?.type !== PromotionType.Offer) {
      return;
    }

    // Note(gsarusi): Any reason other than ApprovedByUser should skip showing
    // regular promotions.
    if (reason !== NoRecommendationReasons.ApprovedByUser) {
      this.skipShowingPromotion = true;
      this.skipShowingPromotionReason = reason;
    }
  }

  // ---------------------------------------------------------------------------
  private async shouldSkipCondition(
    condition: ICondition,
    context: ConditionContext,
  ) {
    if (!condition.shouldSkip) {
      return false;
    }

    const shouldSkip = await condition.shouldSkip(context);
    return shouldSkip;
  }

  // ---------------------------------------------------------------------------
  private triggerNoRecommendation(reason: NoRecommendationReasons) {
    this.logger.info(`No recommendation triggered with reason: ${reason}`);
    for (const listener of this.listeners) {
      if (listener.onNoRecommendations) {
        listener.onNoRecommendations(reason);
      }
    }
  }

  // ---------------------------------------------------------------------------
  private triggerShowPromotion(game: Game, promotion: Promotion) {
    this.logger.info(
      `Triggering recommendation for game: ${game.id} & promotion:` +
        ` ${promotion.id}`,
    );

    for (const listener of this.listeners) {
      if (listener.onShowPromotion) {
        listener.onShowPromotion(game, promotion);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // DelegateProvider
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  addListener(listener: IContentRecommendationServiceDelegate) {
    this.listeners.add(listener);
  }

  // ---------------------------------------------------------------------------
  removeListener(listener: IContentRecommendationServiceDelegate) {
    this.listeners.delete(listener);
  }
}
